/**
 * Searching and ordering the impact-assessment picker. Run through `make web-check`.
 *
 * A native dropdown of 28 publications in one fixed order is hard to scan, and on 30 October 2026
 * an item that was first in the list was reported as missing from it.
 *
 * Kept apart from the component because the rule that matters is invisible to `tsc`: **the chosen
 * item stays in the list whatever the search says.** A `<select>` whose value has no matching
 * option shows something else while `itemId` still holds the old choice, so she would confirm a
 * paid run of a publication the screen was not showing.
 */

export type PickerItem = {
  id: string;
  title: string;
  reference?: string;
  published_at?: string | null;
  captured_at?: string;
  due_date?: string;
};

export const PICKER_SORTS = {
  newest: "Published: newest first",
  oldest: "Published: oldest first",
  added: "Date added: newest first",
  deadline: "Deadline: soonest first",
  az: "Alphabetical: A to Z",
  za: "Alphabetical: Z to A",
} as const;

export type PickerSort = keyof typeof PICKER_SORTS;

/**
 * What the option says, and so what A to Z sorts by: a list sorted by anything other than the
 * words it shows looks unsorted. Many titles already open with their reference
 * ("CP26/38: Financial Crime Guide updates"), which is not worth saying twice.
 */
export function pickerName(item: PickerItem): string {
  const reference = (item.reference || "").trim();
  if (!reference || item.title.toLowerCase().startsWith(reference.toLowerCase())) return item.title;
  return `${reference} — ${item.title}`;
}

/** Lower case, with curly quotes straightened: the FCA writes "firms’" and nobody types that. */
function folded(text: string): string {
  return text.toLowerCase().replace(/[‘’]/g, "'").replace(/[“”]/g, '"');
}

/** Every word must appear in the title or reference, in any order. An empty search matches all. */
export function matchesSearch(item: PickerItem, search: string): boolean {
  const haystack = folded(`${item.reference || ""} ${item.title}`);
  return folded(search)
    .split(/\s+/)
    .filter(Boolean)
    .every((word) => haystack.includes(word));
}

const byName = (a: PickerItem, b: PickerItem) =>
  pickerName(a).localeCompare(pickerName(b), "en-GB", { numeric: true, sensitivity: "base" });

/** `direction` 1 is earliest first. An item without a date goes last in either direction. */
function byDate(a: string, b: string, direction: 1 | -1): number {
  if (!a || !b) return a ? -1 : b ? 1 : 0;
  return a < b ? -direction : a > b ? direction : 0;
}

function comparator(sort: PickerSort): (a: PickerItem, b: PickerItem) => number {
  switch (sort) {
    case "az":
      return byName;
    case "za":
      return (a, b) => byName(b, a);
    case "newest":
      return (a, b) => byDate(a.published_at || "", b.published_at || "", -1);
    case "oldest":
      return (a, b) => byDate(a.published_at || "", b.published_at || "", 1);
    case "added":
      return (a, b) => byDate(a.captured_at || "", b.captured_at || "", -1);
    case "deadline":
      return (a, b) => byDate(a.due_date || "", b.due_date || "", 1);
  }
}

/**
 * The options to show, and how many actually matched. `options` can be one longer than `matches`,
 * because the chosen item stays first even when the search has excluded it.
 */
export function pickerOptions<T extends PickerItem>(
  items: T[],
  search: string,
  sort: PickerSort,
  chosenId = "",
): { options: T[]; matches: number } {
  const order = comparator(sort);
  const found = items
    .filter((item) => matchesSearch(item, search))
    .sort((a, b) => order(a, b) || byName(a, b));
  const chosen = items.find((item) => item.id === chosenId);
  const options = chosen && !found.includes(chosen) ? [chosen, ...found] : found;
  return { options, matches: found.length };
}
